import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiCheck, FiX } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

const ClienteCreditos = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creditStatus, setCreditStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCreditos();
  }, [id]);

  const loadCreditos = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener estado de créditos desde la API
      const result = await clientesService.getCreditStatus(id);

      if (result.success) {
        setCreditStatus(result.data);
      } else {
        setError(result.message || 'Error al cargar el estado de crédito');
      }
    } catch (error) {
      console.error('Error cargando estado de crédito:', error);
      setError('Error de conexión al cargar el estado de crédito');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando estado de crédito..." />;
  }

  return (
    <div className="cliente-creditos-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">ESTADO DE CRÉDITO</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {!creditStatus ? (
        <div className="empty-state">
          <p>No se pudo cargar el estado de crédito</p>
        </div>
      ) : (
        <>
          {/* Alertas */}
          {creditStatus.is_over_limit && (
            <div className="alert alert--danger">
              <FiX />
              <div>
                <strong>Límite de Crédito Excedido:</strong> Este cliente ha excedido su límite de crédito
              </div>
            </div>
          )}
          {creditStatus.has_overdue && (
            <div className="alert alert--warning">
              <FiArrowLeft />
              <div>
                <strong>Facturas Vencidas:</strong> Este cliente tiene {creditStatus.overdue_invoices_count} factura(s) vencida(s)
              </div>
            </div>
          )}

          {/* Información General */}
          <div className="creditos-summary">
            <div className="summary-card">
              <div className="summary-label">Tipo de Pago</div>
              <div className="summary-value">{creditStatus.payment_type}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Términos de Pago</div>
              <div className="summary-value">{creditStatus.payment_terms} días</div>
            </div>
          </div>

          {/* Límite de Crédito */}
          <div className="credit-limit-section" style={{ marginTop: '20px' }}>
            <h3 className="section-title">Límite de Crédito</h3>
            <div className="creditos-summary">
              <div className="summary-card">
                <div className="summary-label">Límite Total</div>
                <div className="summary-value">${creditStatus.credit_limit?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Crédito Usado</div>
                <div className="summary-value">${creditStatus.credit_used?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Crédito Disponible</div>
                <div className="summary-value" style={{ color: creditStatus.credit_available > 0 ? '#10b981' : '#ef4444' }}>
                  ${creditStatus.credit_available?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            {/* Barra de progreso */}
            <div style={{ marginTop: '15px', padding: '0 20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: '0.9em',
                color: '#6b7280'
              }}>
                <span>Uso del Crédito</span>
                <span>{creditStatus.credit_percentage_used?.toFixed(1)}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(creditStatus.credit_percentage_used || 0, 100)}%`,
                  height: '100%',
                  backgroundColor: creditStatus.credit_percentage_used > 90 ? '#ef4444' : creditStatus.credit_percentage_used > 70 ? '#f59e0b' : '#10b981',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
          </div>

          {/* Facturas Pendientes */}
          <div className="pending-invoices-section" style={{ marginTop: '20px' }}>
            <h3 className="section-title">Estado de Facturas</h3>
            <div className="creditos-summary">
              <div className="summary-card">
                <div className="summary-label">Total Pendiente</div>
                <div className="summary-value">${creditStatus.total_pending?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Total Vencido</div>
                <div className="summary-value" style={{ color: creditStatus.total_overdue > 0 ? '#ef4444' : '#6b7280' }}>
                  ${creditStatus.total_overdue?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <div className="creditos-summary" style={{ marginTop: '10px' }}>
              <div className="summary-card">
                <div className="summary-label">Facturas Pendientes</div>
                <div className="summary-value">{creditStatus.pending_invoices_count}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Facturas Vencidas</div>
                <div className="summary-value" style={{ color: creditStatus.overdue_invoices_count > 0 ? '#ef4444' : '#6b7280' }}>
                  {creditStatus.overdue_invoices_count}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/clientes/${id}`} currentTab="creditos" />
    </div>
  );
};

export default ClienteCreditos;
